import './roster-page.css';
import type { CaosTreeNode } from '@caos/ui';
import { renderCollection, type CollectionRow } from './collection-surface.js';
import { readJsonPath, fillPath } from '../predicate.js';
// CAOS-1646 R1 — the projection a list view's derived column already goes through. A THIRD caller,
// which is the whole argument of that module's header, rather than a second implementation here.
import { derivedFromId } from './submission-ref.js';
// CAOS-1734 — the host's MIRROR of the kernel's instant formatter; see that module's header for
// why this is a mirror rather than an import, and what pins the two together.
import { formatInstant, type InstantPrecision } from './instant-format.js';
import { defaultSaveFile, type SaveFile } from './save-file.js';
import {
  renderRosterActionButton,
  actionVisible,
  button as rosterButton,
  type ActionFetch,
  defaultActionFetch as defaultRosterActionFetch,
} from './roster-actions.js';
import type { RosterAction } from './roster-action-types.js';

/**
 * CAOS-382 — the generic RENDERER for an authored `rosterPage` component (kernel/src/meta/metatypes.ts).
 *
 * A `rosterPage` names a control-plane READ route, where the row array sits in that route's payload,
 * and how to read a title/subtitle/id/columns out of each row — the SAME facts every hand-written Setup
 * browse (Roles, Sharing, Value Sets, …) has always hard-coded into a bespoke TypeScript module. This
 * module is the ENGINE side of that split: it takes the AUTHORED spec plus a fetch/navigate seam and
 * draws the SAME shared list surface ({@link renderCollection}) those modules already called by hand.
 *
 * It is deliberately app-agnostic — nothing here mentions Setup — so ANY package can author a
 * `rosterPage` component and get a real, working list surface with zero host code of its own. It is
 * the shared-component-library half of "a Setup screen is authored the same way an app screen is."
 */

export type Navigate = (href: string) => void;

/** The class `setup-surface.css` puts the console inset on. Named once so the two users agree. */
const CONSOLE_PANE = 'setup-pane';

/**
 * CAOS-1764 — put `child` in `root`, and leave the console inset on exactly ONE of them.
 *
 * THE PROBLEM THIS SOLVES, which is a doubling rather than an absence. A route that resolves a
 * roster asynchronously needs a container it can hand back synchronously, and that container has to
 * carry `.setup-pane` itself: it holds the "Loading…" state and the nothing-behind-it fallback, and
 * those would otherwise sit against the frame edge — the symptom this ticket was filed on. But
 * `renderRosterPage` and `renderRosterDetailPage` each root themselves in `.setup-pane` too, because
 * other routes call them directly and cannot rely on a caller to pad them. So the moment the spec
 * resolved, the container put a pane inside a pane and the reader got `var(--caos-space-7)` twice on
 * three sides and 160px at the foot.
 *
 * It is the error `my-profile-surface.ts` and this ticket's own tests both name as "the opposite
 * error and just as visible". Measured, not inferred: before this helper the two padding tests
 * counted 2 panes where they assert 1.
 *
 * EVERY CONTAINER WITH THIS SHAPE, enumerated rather than described, because the first version of
 * this comment said "every roster route" while fixing only two files and a review caught it:
 *
 *   - `roster-destination.ts` — the `feedback` app tab, the package-root destination, and
 *     My Profile → Feedback (via `my-profile-surface.ts`)
 *   - `setup-surface.ts` — every `rosterPage`-backed Setup claim, e.g. Feedback Triage
 *   - `setup-object-manager.ts` — Object Manager, both the list and the per-object detail
 *   - `setup-seats.ts` — Seat Allocation
 *   - `setup-component-library.ts` — Component Library
 *
 * `toggle` rather than `remove` is what makes it re-entrant, and that matters in
 * `roster-destination.ts` specifically: there a rejection from `await drawn` AFTER a surface has
 * been placed falls into the `catch`, which writes a fallback carrying no inset — so the container
 * has to take the class BACK rather than having dropped it permanently on the first write. The three
 * `setup-*` containers above each write exactly once per render (every branch returns), so for them
 * the toggle is uniformity rather than necessity, and only their pane-carrying writes route through
 * here; their `notAvailable`/`renderPage` branches keep the container's own inset because the child
 * has none.
 */
export function placeInConsolePane(root: HTMLElement, child: Node): void {
  const childCarriesTheInset = child instanceof HTMLElement && child.classList.contains(CONSOLE_PANE);
  root.classList.toggle(CONSOLE_PANE, !childCarriesTheInset);
  root.replaceChildren(child);
}

export interface RosterPageSpec {
  readonly key: string;
  readonly label: string;
  readonly eyebrow?: string;
  readonly description?: string;
  readonly read: { readonly noun: string; readonly verb: string };
  readonly rowsAt?: string;
  readonly idField: string;
  readonly titleField: string;
  readonly subtitleField?: string;
  readonly columns: readonly RosterColumnSpec[];
  readonly searchPlaceholder?: string;
  /**
   * CAOS-704 — the row paths a search matches; absent ⇒ title, subtitle and id; empty ⇒ no search box.
   *
   * CAOS-1717 — AND NOT THE WHOLE OF SEARCH once a column declares a `secondary`. Such a column
   * contributes BOTH of its values to the row's search text, appended to whatever is listed here, so
   * naming a narrower set does not exclude them.
   *
   * That is deliberate rather than incidental. The case this came from was a path that sat in
   * `searchFields` and appeared on no screen; making it readable as a second line must not be undone
   * by the obvious next edit, which is to drop it from this list. Both lines are included because
   * the state being replaced was one of the two being findable and the other not.
   *
   * THE COST, stated because it is not obvious from the field name: `searchFields` cannot NARROW a
   * column that carries a secondary. If a roster needs a visible second line that is deliberately
   * not searchable, that is not expressible today and this rule would have to grow a way to say it.
   *
   * Pinned in `host/test/caos-1717-a-second-line-under-a-column.test.ts`, and it takes FOUR tests
   * rather than the three that were there first. The original three all declared
   * `searchFields: ['description']` while the secondary column's own primary was `valueAt:
   * 'description'`, so the one named for finding a row by its first line matched through
   * `searchFields` alone and passed whether or not the primary was appended. The fourth test omits
   * that path from `searchFields` and searches for the primary's text, which is the only form that
   * can tell the two behaviours apart.
   */
  readonly searchFields?: readonly string[];
  readonly emptyLabel: string;
  readonly searchEmptyLabel?: string;
  readonly detailRoute?: string;
  /** CAOS-714 — a board's columns, in order; absent ⇒ no board reading is offered. */
  readonly stages?: readonly { readonly value: string; readonly label: string }[];
  /** JSON path (within a row) to the stage it stands in. */
  readonly stageField?: string;
  /** CAOS-714 — the ways this roster may be gathered, each a JSON path read per row. */
  readonly groupBy?: readonly { readonly key: string; readonly label: string; readonly valueAt: string }[];
  readonly groupByDefault?: string;
  readonly sortOptions?: readonly { readonly value: string; readonly label: string }[];
  readonly defaultMode?: 'card' | 'table' | 'kanban' | 'split' | 'group';
  /** CAOS-704 — up to two labelled facts along the foot of a card, apart from the table's columns. */
  readonly cardFields?: readonly { readonly label: string; readonly valueAt: string }[];
  /** CAOS-337 — segmented status-style filter tabs; see the schema doc for the `equals: null` "All" convention. */
  readonly filterField?: string;
  readonly filters?: readonly RosterFilter[];
  readonly filterDefault?: number;
  /** CAOS-551 — an opt-in CSV of the rows on screen; absent means no export is offered. */
  readonly download?: RosterDownload;
  /** CAOS-337/CAOS-743 — page-level buttons (e.g. "Invite user") drawn beside the list heading. */
  readonly pageActions?: readonly RosterAction[];
  /** CAOS-1501 — JSON path (within a row) to the id {@link RosterPageSpec.rowAction} acts on. */
  readonly rowIdField?: string;
  /** CAOS-1501 — one action offered on every row; see {@link ROW_ACTION_COLUMN}. */
  readonly rowAction?: RosterAction;
  /** CAOS-698 — the rows drawn again as a tree under the list; see {@link orderHierarchy}. */
  readonly hierarchy?: RosterHierarchy;
}

/**
 * One roster column. The metatype refuses a column with neither `valueAt` nor `cases`, and one with
 * both, so {@link rosterColumnValue} may treat "no `valueAt`" as "it folds `cases`".
 */
export interface RosterColumnSpec {
  readonly key: string;
  readonly label: string;
  /** JSON path (within a row) this column reads. Absent ⇒ it folds `cases` instead. */
  readonly valueAt?: string;
  /**
   * CAOS-485 — the value at `valueAt` holds an ENCODED document; read `at` inside it.
   *
   * For a route that answers with a stored envelope rather than with the fields inside it. The
   * application log is the case that needed it: `logs app` returns `detail` as the `app-log` JSON
   * verbatim, and says why — a reader that parsed it there "would make this the second place that
   * understands the envelope's shape". So the decode belongs to the surface, and the PACKAGE names
   * the path while the host only decodes. Absent ⇒ `valueAt` is read as it always was.
   */
  readonly decode?: { readonly at: string };
  /** CAOS-1646 R1 — project the value at `valueAt` the way a list view's derived column does. */
  readonly derive?: { readonly prefix?: string; readonly length?: number; readonly transform?: 'upper' | 'lower' };
  /** CAOS-1646 R2 — fold this column's value out of several paths, first match winning. */
  readonly cases?: readonly {
    readonly at: string;
    readonly equals?: string;
    readonly contains?: string;
    readonly value: string;
  }[];
  /** The word when no case matched. */
  readonly otherwise?: string;
  /**
   * CAOS-1734 — render the value at `valueAt` as a date a person reads, at this precision.
   *
   * The grammar refuses it beside `cases` or `derive`, so when it is set there is a stored value
   * to render. Absent ⇒ the value is rendered exactly as it is today.
   */
  readonly format?: InstantPrecision;
  /**
   * CAOS-1717 — a second value drawn beneath the primary one, subordinate to it, in TABLE mode.
   *
   * The four RESOLUTION keys and none of the presentation ones, so a second line can fold a cascade
   * exactly as a first line can but cannot become a chip or a link competing with the value above
   * it. Absent ⇒ one line, which is every column authored before this.
   */
  readonly secondary?: RosterColumnValueSource;
  readonly align?: 'start' | 'end';
  readonly chip?: boolean;
  readonly link?: boolean;
}

/**
 * CAOS-1717 — the keys that RESOLVE a value, named apart from the column that presents it.
 *
 * `RosterColumnSpec` holds this shape twice over: inline for its primary value (where the keys have
 * always lived) and under `secondary`. Naming it lets {@link resolveValue} be one function rather
 * than two that drift, which is the whole reason a second line gets `derive` and `cases` for free.
 */
export interface RosterColumnValueSource {
  readonly valueAt?: string;
  /** CAOS-485 — read a path inside the encoded document at `valueAt`, exactly as a column's primary `decode` does. */
  readonly decode?: { readonly at: string };
  readonly derive?: { readonly prefix?: string; readonly length?: number; readonly transform?: 'upper' | 'lower' };
  readonly cases?: readonly {
    readonly at: string;
    readonly equals?: string;
    readonly contains?: string;
    readonly value: string;
  }[];
  readonly otherwise?: string;
  /** CAOS-1734 — render this value as a date, exactly as a primary value's `format` does. */
  readonly format?: InstantPrecision;
}

/**
 * CAOS-1646 — ONE place a roster column's displayed value is computed.
 *
 * It is one function because three callers must agree about it and the code already says so out
 * loud: `rosterCsv`'s header promises the export reads values "EXACTLY as `projectRow` reads them …
 * so the file and the screen cannot say different things about a row". Before this there was
 * nothing to share — every column was `readJsonPath(row, valueAt)` — and a derived value computed
 * in the table alone would have broken that promise silently, handing somebody a spreadsheet of raw
 * uuids under a column headed Reference.
 *
 * The third caller is the filter: a tab may now ask its question of a column, and it has to ask it
 * of the SAME word the cell shows.
 */
export function rosterColumnValue(row: Record<string, unknown>, column: RosterColumnSpec): string {
  return resolveValue(row, column);
}

/**
 * CAOS-1717 — the resolution itself, over anything carrying {@link RosterColumnValueSource}'s keys.
 *
 * Lifted out of `rosterColumnValue` unchanged so a column's SECOND value goes through the identical
 * cascade, projection and fallthrough as its first. A second resolver written beside this one would
 * have been the cheaper change and the wrong one: the primary's rules are not obvious — `contains`
 * splits list membership from substring, and `derive` returns the empty string rather than a short
 * code when there are too few hex digits — and a copy would have had to re-derive each of them
 * correctly, silently, with nothing on screen showing which copy a value came from.
 */
/*
 * CAOS-1764 — the SOURCE is `unknown` rather than `Record<string, unknown>`, because the third caller
 * reads a route's whole response rather than a row.
 *
 * A roster column resolves against a row, which is always an object. A detail page's labelled row
 * resolves against the page PAYLOAD, which is whatever the read route answered — `unknown` is what
 * `renderRosterDetailPage` holds it as, and correctly, since a malformed metadata case can hand it
 * anything. Widening the parameter rather than casting at that one call site is the honest spelling:
 * every path below goes through `readJsonPath`, whose own parameter has been `unknown` from the
 * start and which returns `undefined` for a segment it cannot walk. Nothing here ever needed the
 * narrower type, and asserting one at the boundary would have claimed a guarantee the caller does
 * not have. Every existing caller passes a row and is unaffected.
 */
/**
 * CAOS-485 — read one path inside a value that arrived ENCODED.
 *
 * Two inputs are both legitimate and they are handled deliberately rather than by accident:
 *
 *  - A STRING holding JSON, which is the case that exists today. `logs app` returns the stored
 *    `app-log` envelope verbatim, so `detail` is a string and `readJsonPath` — which walks objects —
 *    cannot see inside it.
 *  - An OBJECT, which is what the same value becomes if a route ever answers with the envelope
 *    already decoded. Reading the path straight out of it is not leniency about a mis-declaration:
 *    it is what stops a column going silently blank on a change made in the kernel, which is the
 *    failure this whole key exists to prevent at one remove.
 *
 * A string that does not parse comes back AS ITSELF, the same choice `format` makes one function
 * below and for the same stated reason: a column of empty cells reads as data that has not arrived
 * and nobody investigates it, whereas a cell showing an envelope nothing could parse is visibly
 * wrong. That is deliberately NOT the same as a path which is simply absent inside a document that
 * parsed fine — an entry that logged no `fields` has no `fields.quoteId`, and an empty cell is the
 * honest answer there, exactly as it is for any absent path.
 */
function decodedAt(stored: unknown, at: string): unknown {
  if (stored === null || stored === undefined) return undefined;
  if (typeof stored === 'object') return readJsonPath(stored, at);
  if (typeof stored !== 'string') return stored;
  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return stored;
  }
  // A document that parses to a scalar has no path to walk, and `readJsonPath` would answer
  // `undefined` for every `at`. Returning it as it was found keeps the "visibly wrong, not blank"
  // rule above true for an envelope that is JSON but not a document.
  if (parsed === null || typeof parsed !== 'object') return stored;
  return readJsonPath(parsed, at);
}

export function resolveValue(row: unknown, column: RosterColumnValueSource): string {
  if (column.cases !== undefined) {
    for (const branch of column.cases) {
      const raw = readJsonPath(row, branch.at);
      if (branch.contains !== undefined) {
        const hit = Array.isArray(raw)
          ? raw.some((entry) => String(entry) === branch.contains)
          : String(raw ?? '').includes(branch.contains);
        // `contains` matches a LIST by MEMBERSHIP and a plain string by substring — the same split
        // `rowMatchesFilter` draws, and for the same reason: a tab asking for one token must not be
        // satisfied by a neighbouring token that merely contains it.
        if (hit) return branch.value;
        continue;
      }
      if (branch.equals !== undefined && String(raw ?? '') === branch.equals) return branch.value;
    }
    return column.otherwise ?? '';
  }
  const stored = readJsonPath(row, column.valueAt ?? '');
  // CAOS-485 — the decode happens FIRST, because it changes WHICH value the two shaping keys below
  // shape. `derive` projecting a record id out of the envelope's `fields`, and `format` rendering an
  // instant from inside it, both want the decoded value and never the envelope: projecting the
  // envelope would strip it to its own hex characters, and no envelope parses as a date.
  const text = String((column.decode ? decodedAt(stored, column.decode.at) : stored) ?? '');
  // The projection is the SHARED one — `derivedFromId`, which the intake confirmation and the list
  // view's derived column already go through. Its absence rule is the part that matters and is
  // inherited rather than restated: too few hex digits yields the empty string, never a short code
  // that looks complete.
  if (column.derive) return derivedFromId(text, column.derive);
  // CAOS-1734 — and the OTHER shaping axis, which the grammar refuses to let an author declare
  // alongside `derive` or `cases`, so this is an `else` rather than a precedence decision taken
  // here. A value `formatInstant` cannot parse comes back as itself, not as a blank cell: a column
  // of empty cells reads as data that has not arrived, which is the worse of the two wrong answers
  // and the one nobody investigates.
  if (column.format) return formatInstant(text, column.format) ?? text;
  return text;
}

/**
 * CAOS-1717 — a column's SECOND value for one row, or the empty string when it declares none.
 *
 * Empty is the "draw one line" signal, and it covers two different cases on purpose: a column with
 * no `secondary` at all, and a `secondary` whose path is absent on THIS row. The second is the one
 * worth stating — a value that is present on most rows and missing on a few must leave those few as
 * single-line cells rather than cells with an empty second line reserving space under them, because
 * a column of ragged blank lines reads as a rendering fault.
 */
export function rosterColumnSecondary(row: Record<string, unknown>, column: RosterColumnSpec): string {
  return column.secondary ? resolveValue(row, column.secondary) : '';
}

/**
 * CAOS-1717 — how a second value joins its first OUTSIDE a table: in a card's detail list and in the
 * CSV export, where a cell is one string and cannot be two lines.
 *
 * The export matters more than it looks. `rosterCsv`'s own header promises the file and the screen
 * "cannot say different things about a row", and a column whose screen cell shows two values while
 * its exported cell shows one is the softer form of exactly that. The alternative — a second CSV
 * column — would need a second header label the author never wrote.
 */
const SECONDARY_JOIN = ' — ';

/**
 * CAOS-1717 — what an empty cell reads as, mirroring what `caos-data-table` draws for one itself.
 *
 * The table supplies this already: `fillCell` appends an em dash when a cell's value is an empty
 * string. It does NOT do so for a `{ node }` descriptor, which it appends as given — so a stacked
 * cell has to carry the placeholder itself or an absent primary becomes a blank line rather than a
 * visibly empty one. Duplicated here deliberately, because the alternative is reaching into the
 * component's private rendering.
 */
const EMPTY_CELL = '—';

/**
 * CAOS-1717 — the two parts of one column as one string, joined ONLY when both are present.
 *
 * An empty primary with a present secondary used to produce ` — x`: a separator with nothing on its
 * left, in the exported CSV and in the split view's label/value pair. The guard is on BOTH sides
 * because either can be absent — a path that does not resolve gives the empty string, not undefined.
 *
 * The table's em dash is NOT reproduced here. It is a placeholder for the eye, not data: an exported
 * field for a value that is not there should be empty rather than contain a dash someone then has to
 * strip. So on screen an absent primary reads as `—` with the secondary beneath it, while in the CSV
 * and the detail pair the secondary stands alone. The three vehicles still agree about what the
 * column SAYS; they differ only in how each one spells "nothing".
 */
function joinParts(primary: string, secondary: string): string {
  if (!secondary) return primary;
  if (!primary) return secondary;
  return `${primary}${SECONDARY_JOIN}${secondary}`;
}

/** CAOS-1717 — the primary and secondary of one column as a single string, for a CSV or card cell. */
export function joinedColumnValue(row: Record<string, unknown>, column: RosterColumnSpec): string {
  return joinParts(rosterColumnValue(row, column), rosterColumnSecondary(row, column));
}

/**
 * CAOS-1717 — stack a column's second value under its first, in a table cell.
 *
 * Takes the cell value the column would have produced WITHOUT a secondary — a bare string, or a
 * `{ node }` holding a link or a chip — and returns it untouched when there is no second value. That
 * shape is the point: every existing cell keeps the exact representation it had, including a bare
 * string where the table draws an em dash for the empty case, and only a column that asked for a
 * second line gets a wrapper.
 *
 * STYLED INLINE, not through `setup-surface.css`. `caos-data-table` renders into an open shadow root
 * (`CaosElement` attaches one), so a page stylesheet does not reach a node handed to it as a cell —
 * which is already visible in this file: `link()` sets `class="setup-link"` and that class has never
 * applied inside a table cell. CSS CUSTOM PROPERTIES do cross the boundary, so the dim tone is the
 * shared `--caos-color-text-muted` token rather than a literal colour, and the size is relative so
 * it follows whatever the table sets rather than fighting it.
 */
export function stackSecondary(cell: unknown, secondary: string): unknown {
  if (!secondary) return cell;
  const wrap = document.createElement('div');
  // `setProperty` throughout rather than the typed accessors: a `var(...)` value assigned through
  // `style.color` is dropped by some CSSOM implementations (jsdom among them, which is what the
  // tests render in), and a silently dropped declaration is a styling bug that passes its own test.
  wrap.style.setProperty('display', 'grid');
  wrap.style.setProperty('row-gap', '2px');
  // A grid child's default `min-width: auto` refuses to shrink below its content, which in a table
  // cell means a long second line widens the column instead of wrapping inside it.
  wrap.style.setProperty('min-width', '0');
  // AN EMPTY PRIMARY STILL HAS TO READ AS EMPTY, AND "EMPTY" IS ABOUT WHAT THE CELL SHOWS rather
  // than which shape it arrived in. `caos-data-table` draws an em dash for a cell whose value is an
  // empty string, but only on that path — `fillCell` appends a `{ node }` descriptor as given, with
  // no empty check — so wrapping the pair hands it a node and the placeholder never appears.
  //
  // THE FIRST VERSION OF THIS ONLY COVERED THE BARE-STRING BRANCH, which left the two shapes that
  // are always nodes uncovered: `cellValue` returns `{ node: caos-tag }` for a `chip` column and
  // `{ node: link(...) }` for a linked one EVEN WHEN THE TEXT IS EMPTY, and the detail page's
  // `rowHref` branch hands in `{ node: rowLink(...) }` unconditionally. All three drew an empty
  // element above the second line. Asking the cell whether it renders anything covers every call
  // site at once, instead of each one having to remember to say so.
  //
  // AND "SHOWS SOMETHING" IS TEXT **OR** AN ELEMENT CHILD, which is a mechanism rather than the
  // promise this comment used to make. It previously said that a node cell with empty text but
  // visible content — an icon, say — "would need to opt out", and there was no opt-out: no flag,
  // no parameter, no test. A sentence guaranteeing behaviour that nothing enforces is the same
  // defect as a test whose name states more than its fixture can reach, one level out, and the
  // next icon-bearing cell would have been silently replaced by a dash with only prose against it.
  //
  // So the predicate decides it instead: a node renders something if it has text or if it has an
  // element child. An icon cell is an `<svg>` or a custom element, so it has a child and is kept;
  // the three empty cases that motivated the placeholder — an empty `caos-tag`, an empty `link()`
  // and an empty `rowLink()` — have no element children and still get the dash. No new surface,
  // nothing to remember at a call site, and it is pinned by a test rather than by this paragraph.
  //
  // What is deliberately NOT covered: a node that renders through CSS alone — a background image
  // on an empty span, say. That cannot be detected without layout, and a cell drawn entirely by a
  // stylesheet is a thing to refuse rather than to accommodate.
  const first = document.createElement('span');
  const node =
    cell != null && typeof cell === 'object' && (cell as { node?: Node }).node != null
      ? (cell as { node: HTMLElement }).node
      : undefined;
  const nodeShowsSomething =
    node !== undefined && ((node.textContent ?? '').length > 0 || node.childElementCount > 0);
  const shown = node !== undefined ? (node.textContent ?? '') : cell == null ? '' : String(cell);
  if (node !== undefined ? !nodeShowsSomething : shown.length === 0) {
    first.textContent = EMPTY_CELL;
  } else if (node !== undefined) {
    first.append(node);
  } else {
    first.textContent = shown;
  }
  const second = document.createElement('span');
  second.className = 'sa-list__cell-secondary';
  second.style.setProperty('color', 'var(--caos-color-text-muted)');
  // Relative, so the subordinate line follows whatever size the table sets rather than fighting it
  // with a literal px that is right in one surface and wrong in the next.
  second.style.setProperty('font-size', '0.9em');
  second.style.setProperty('min-width', '0');
  second.style.setProperty('overflow-wrap', 'anywhere');
  second.textContent = secondary;
  wrap.append(first, second);
  return { node: wrap };
}

/**
 * CAOS-337/CAOS-551 — one segmented filter tab. Exactly one of `equals`/`contains` is declared; the
 * metatype refuses a tab declaring neither or both.
 */
export interface RosterFilter {
  /** The literal `filterField` value this tab narrows to; `null` is the roster's own "All" tab. */
  readonly equals?: string | null;
  /** CAOS-551 — the entry a LIST-valued `filterField` must hold for the row to appear. */
  readonly contains?: string;
  readonly label: string;
  /**
   * CAOS-1646 R2 — ask this tab's question of the named COLUMN's value rather than of the roster's
   * `filterField` path. The metatype refuses a `column` the surface does not show.
   */
  readonly column?: string;
}

/**
 * CAOS-1646 R2 — the value one filter tab interrogates for one row.
 *
 * A tab naming a `column` reads that column's computed value, so a tab over a FOLDED word narrows
 * by the same word the cell shows. Anything else reads `filterField`, exactly as every tab did
 * before this.
 *
 * A tab naming a column the surface does not show returns `undefined` rather than guessing. That
 * is refused at deploy, so reaching it means a partial read; `rowMatchesFilter` then treats it as
 * an unmatched value rather than narrowing by the empty string, which would hide every row.
 */
export function filterTabValue(
  row: Record<string, unknown>,
  tab: RosterFilter,
  columns: readonly RosterColumnSpec[],
  filterField?: string,
): unknown {
  if (tab.column !== undefined) {
    const column = columns.find((c) => c.key === tab.column);
    if (!column) return undefined;
    // A column that COMPUTES — a cascade or a projection — yields one word, and that word is what
    // the tab must ask about. A column that merely reads a path shows whatever is at that path, so
    // the tab reads it RAW: `rowMatchesFilter` matches a list by membership and a string by
    // substring, and stringifying first would collapse a list into `"admin,auditor"` and quietly
    // turn `contains: 'admin'` into a substring test that a neighbouring `administrator` satisfies.
    // That is the same distinction `rosterColumnValue`'s own cascade draws, kept on this path too,
    // and it leaves a `column` tab and a `filterField` tab over the same list behaving alike.
    // CAOS-1734 — `format` joins that list, and leaving it out was a real defect for the hour it
    // existed here. A formatted column COMPUTES the word in its cell, so a tab reading the path raw
    // would ask about `2026-10-16T18:46:22.027Z` while the cell says "Oct 16, 2026" — the exact
    // disagreement this branch exists to prevent, arriving through a third shaping axis rather than
    // the two it was written for.
    // CAOS-485 — and `decode` joins it, for exactly the reason the paragraph above gives for
    // `format`. A decoded column COMPUTES its word: the cell says `the outward call was refused`
    // while the path holds the whole `app-log` envelope, so a tab reading raw would ask its question of a
    // JSON document and match nothing — or, with `contains`, match on a substring that happens to
    // appear somewhere else in the envelope. A severity tab over a decoded `level` is the first
    // thing the Logger app's entry list needs, so this is the case rather than a hypothetical.
    if (
      column.cases === undefined &&
      column.derive === undefined &&
      column.format === undefined &&
      column.decode === undefined
    ) {
      return readJsonPath(row, column.valueAt ?? '');
    }
    return rosterColumnValue(row, column);
  }
  return filterField === undefined ? undefined : readJsonPath(row, filterField);
}

/**
 * CAOS-1646 R2 — whether this surface can narrow at all with the tab that is active.
 *
 * `filters && filterField` was the gate, which is wrong once a tab can name a column: a roster
 * whose tabs all ask of a folded word has no `filterField` to declare, and the old gate would have
 * drawn its tabs and then filtered nothing — tabs that move the highlight and leave every row on
 * screen. Asking about the ACTIVE tab is what makes a roster able to mix the two.
 */
export function canNarrow(
  filters: readonly RosterFilter[] | undefined,
  active: number,
  filterField?: string,
): boolean {
  if (!filters || filters.length === 0) return false;
  return filters[active]?.column !== undefined || filterField !== undefined;
}

/** CAOS-551 — an opt-in CSV of the rows on screen. */
export interface RosterDownload {
  /** The name the file lands under, extension included. */
  readonly filename: string;
  /** The button's wording; absent means "Export". */
  readonly label?: string;
}

/** CAOS-698 — how a roster's rows form a tree, and the count each tree row carries. */
export interface RosterHierarchy {
  readonly heading: string;
  /** JSON path (within a row) to its parent's id, matched against the other rows' `idField`. */
  readonly parentField: string;
  /** `{count}` in a wording is replaced with the number read at `valueAt`. */
  readonly count?: { readonly valueAt: string; readonly zero: string; readonly one: string; readonly other: string };
}

/**
 * CAOS-1501 — the column key the per-row action button occupies.
 *
 * Prefixed so it cannot collide with an authored column: a spec's own `columns[].key` values come
 * from the package author, and a roster that happened to declare a column called `actions` would
 * otherwise have its cells silently overwritten by the button.
 */
export const ROW_ACTION_COLUMN = '__rowAction';

export interface RosterFetchResponse {
  json(): Promise<unknown>;
}
export type RosterFetch = (path: string) => Promise<RosterFetchResponse>;

export const defaultRosterFetch: RosterFetch = (path) =>
  fetch(path, { credentials: 'same-origin' }) as unknown as Promise<RosterFetchResponse>;

interface Envelope {
  ok?: boolean;
  data?: unknown;
  error?: { message?: string };
}

/**
 * CAOS-1739 — the wording every list shows when it has no rows to show.
 *
 * Deliberately says only that it could not be loaded, and does NOT carry the envelope's own
 * `error.message` into it. That is the opposite of the choice CAOS-1502 made for the confirm
 * dialog, which renders the kernel's sentence verbatim, and the difference is who is reading and
 * what they can do about it. A refusal in a dialog answers a question somebody just asked, so the
 * kernel's reason is the most useful thing on screen. This is a list that did not load, and the
 * kernel's reasons for that routinely name a grant, a route or a package key — `setup.access is
 * required`, which is true and means nothing to the person reading it.
 */
const READ_FAILED_LABEL = 'This list could not be loaded. Try again in a moment.';

/**
 * CAOS-1739 — what a read of the rows came back as. THREE outcomes, not two.
 *
 * Before this, `fetchRows` returned `Record<string, unknown>[]` and answered a thrown fetch, a
 * non-ok envelope and a genuinely empty list all with `[]`. An empty array is exactly what makes
 * the renderer draw the spec's `emptyLabel`, so a customer who could not see their data was told
 * they had none — a different and much worse statement, and one that a refresh appears to "fix",
 * which is how it stayed unreported.
 *
 * THE SAME THREE-OUTCOME CONTRACT `exchange-catalog.ts` ALREADY USES, for the same reason recorded
 * there: that surface reads a route gated on `setup.access`, so a refusal is its commonest answer,
 * and reporting it as "nothing has been published" would be a confident false statement about the
 * whole marketplace made to the person least able to check it. Every list on the platform has that
 * property; only that one surface had the contract. `roster-actions.ts` reached the same shape
 * independently for the uninstall preview (`failed`/`empty` as separate fields, CAOS-1502), which
 * is two incumbents rather than a new idea.
 */
type RowsOutcome =
  | { readonly ok: true; readonly rows: Record<string, unknown>[] }
  | { readonly ok: false };

/**
 * Read the read route's payload. Never crashes and never renders stale rows — but a failure is
 * reported as a failure rather than as an absence of rows.
 */
async function fetchRows(spec: RosterPageSpec, doFetch: RosterFetch): Promise<RowsOutcome> {
  let env: Envelope;
  try {
    const res = await doFetch(`/api/${spec.read.noun}/${spec.read.verb}`);
    env = (await res.json()) as Envelope;
  } catch {
    return { ok: false };
  }
  if (env?.ok !== true) return { ok: false };
  const raw = spec.rowsAt ? readJsonPath(env.data, spec.rowsAt) : env.data;
  /*
   * NO ARRAY WHERE THE SPEC SAYS THE ROWS ARE IS A FAILED READ, NOT AN EMPTY ONE.
   *
   * The first version of this fix said the opposite — that `ok: true` means the route answered, so a
   * `rowsAt` naming a path the payload does not carry is the spec disagreeing with the route, an
   * authoring mistake rather than an outage. A review of #910 pointed out that `page-surface.ts`
   * already settled this same ambiguity the OTHER way, explicitly, under CAOS-788:
   *
   *     // An `undefined` here means the payload did not hold what the page said it would — a
   *     // different statement from "it held an empty list", and the two must not collapse.
   *     if (rows === undefined || rows === null) throw new Error(…)
   *
   * And the sibling surface CAOS-788's own comment describes — the one that "read `rowsAt` straight
   * off the body, against a shape no route sends, and drew empty behind a green suite" — is THIS
   * file. So the precedent is not merely nearby, it was written about this exact failure.
   *
   * The reviewer's argument is the one that settles it: `readJsonPath` cannot tell an author's typo
   * from a route that answered `ok: true` and genuinely omitted the field. The two are
   * indistinguishable here, so the choice is which way to be wrong when it cannot be known — and
   * this whole ticket exists because the wrong way is telling somebody they have no data.
   *
   * Extended one step past CAOS-788's rule, which speaks only of `undefined`/`null`: a value that is
   * PRESENT but not an array is also a failure. A roster cannot draw a string or an object as rows,
   * and "present but the wrong shape" is further from "empty" than "absent" is.
   *
   * CHECKED AGAINST THE ROUTES BEFORE CHANGING IT, because this is only safe if no route omits its
   * rows key when the list is genuinely empty — that would turn a real empty org into a false
   * "could not be loaded", which is this defect pointing the other way. `packages/list` builds
   * `return { packages }` from `describePackageInstalls`, which always yields an array, and a live
   * read of an empty org answered `{"packages":[]}` — the key present, the array explicit.
   */
  if (!Array.isArray(raw)) return { ok: false };
  return {
    ok: true,
    rows: raw.filter((r): r is Record<string, unknown> => r != null && typeof r === 'object'),
  };
}

/**
 * CAOS-337/CAOS-551 — whether one row belongs under the active tab.
 *
 * `contains` matches a LIST-valued field by MEMBERSHIP rather than by text, and that distinction is
 * the whole reason it exists rather than being a substring test: the populations a People roster
 * asks for are tokens, and a tab asking for one must never be satisfied by a neighbouring token
 * that merely contains it. A plain string field still falls back to a substring test, because a
 * roster whose author joined its values into one field asked a text question.
 *
 * A tab declaring neither shows everything — what `equals: null` means, and the safest reading of a
 * declaration the metatype already refuses.
 */
export function rowMatchesFilter(tab: RosterFilter, value: unknown): boolean {
  if (tab.contains !== undefined) {
    return Array.isArray(value)
      ? value.some((entry) => String(entry) === tab.contains)
      : String(value ?? '').includes(tab.contains);
  }
  if (tab.equals === null || tab.equals === undefined) return true;
  return String(value ?? '') === tab.equals;
}

/**
 * CAOS-551 — the rows on screen as CSV: the roster's declared columns, in order, for the rows
 * handed in.
 *
 * The values are read EXACTLY as {@link projectRow} reads them for the table — same paths, same
 * `String(... ?? '')` — so the file and the screen cannot say different things about a row. A cell
 * the screen draws as a chip or a link exports as its text, which is that same text.
 */
export function rosterCsv(
  columns: readonly RosterColumnSpec[],
  rows: readonly Record<string, unknown>[],
): string {
  const lines = [columns.map((c) => csvField(c.label))];
  for (const row of rows) {
    // CAOS-1646 — the same `rosterColumnValue` the table uses. This line read `valueAt` directly,
    // which is what the header above promises it must not do once a value can be derived: a column
    // headed Reference would have exported the raw uuid the screen does not show.
    // CAOS-1717 — and its SECOND value in the same cell when it has one, because this function's
    // own header promises the file and the screen cannot say different things about a row.
    lines.push(columns.map((c) => csvField(joinedColumnValue(row, c))));
  }
  // CRLF, per RFC 4180: the line ending a spreadsheet on any platform reads without argument.
  return lines.map((cells) => cells.join(',')).join('\r\n');
}

/** One CSV field: quoted when it has to be, and never handed to a spreadsheet as a formula. */
function csvField(value: string): string {
  // A leading =, +, -, @, tab or CR makes a cell a FORMULA in Excel and Sheets, which then runs on
  // the reader's machine instead of being read. A roster's values are typed by people — a display
  // name arrives with an invitation — so this is text from outside the org reaching a spreadsheet.
  // A leading apostrophe is the standard defence, and is what a spreadsheet strips on display.
  const text = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /["\n\r,]/.test(text) ? `"${text.split('"').join('""')}"` : text;
}

/**
 * How a built CSV reaches the reader's disk; seamed so a test can measure the file, not the
 * browser.
 *
 * CAOS-1837 \u2014 the seam itself moved to `save-file.ts` so an ACTION's response can carry a file
 * through the same function (see that module's header). Re-exported here because this is where
 * every existing reader asks for it, and the download button below is still its first caller.
 */
export { type SaveFile, defaultSaveFile };

function link(text: string, href: string, navigate?: Navigate): HTMLAnchorElement {
  const a = document.createElement('a');
  a.className = 'setup-link';
  a.href = href;
  a.textContent = text;
  a.addEventListener('click', (ev) => {
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    ev.preventDefault();
    navigate?.(href);
  });
  return a;
}

/** A plain-text, chip, or link cell, per the column's declared `chip`/`link` (link takes precedence). */
function cellValue(
  text: string,
  col: { readonly chip?: boolean; readonly link?: boolean },
  href: string | undefined,
  navigate?: Navigate,
): unknown {
  if (col.link && href) return { node: link(text, href, navigate) };
  if (col.chip) {
    const el = document.createElement('caos-tag');
    el.textContent = text;
    return { node: el };
  }
  return text;
}

/** Project one row into a {@link CollectionRow} purely from the spec's declared JSON paths. */
function projectRow(spec: RosterPageSpec, row: Record<string, unknown>, navigate?: Navigate): CollectionRow {
  const id = String(readJsonPath(row, spec.idField) ?? '');
  const title = String(readJsonPath(row, spec.titleField) ?? id);
  const subtitle = spec.subtitleField ? String(readJsonPath(row, spec.subtitleField) ?? '') : undefined;
  const href = spec.detailRoute ? fillPath(spec.detailRoute, { id, ...row }).path : undefined;
  // CAOS-714 — the board column and the grouping lanes a row stands in, each read the same way
  // every other value on the row is: a JSON path the spec names, never derived here.
  const stageValue = spec.stageField ? String(readJsonPath(row, spec.stageField) ?? '') : undefined;
  const groups: Record<string, string> | undefined =
    spec.groupBy && spec.groupBy.length > 0
      ? Object.fromEntries(spec.groupBy.map((g) => [g.key, String(readJsonPath(row, g.valueAt) ?? '')]))
      : undefined;

  const cells: Record<string, unknown> = {};
  const detail: { label: string; value: string }[] = [];
  // CAOS-1717 — what a column's own values add to this row's search text; see `searchText` below.
  const columnSearch: string[] = [];
  for (const col of spec.columns) {
    // CAOS-1646 — through the shared resolver, so a projected or folded value reaches the cells,
    // the card detail and the CSV as one answer.
    const text = rosterColumnValue(row, col);
    // CAOS-1717 — one line unless the column asked for two; `stackSecondary` returns the cell it was
    // given when there is no second value, so a column without one is byte-identical to before.
    const secondary = rosterColumnSecondary(row, col);
    cells[col.key] = stackSecondary(cellValue(text, col, href, navigate), secondary);
    // A card has no cells, so its detail list carries both values joined — the same string the CSV
    // exports, so the three vehicles agree about what this column says.
    detail.push({ label: col.label, value: joinParts(text, secondary) });
    // GATED ON THE SPEC, NOT ON THIS ROW'S VALUE. `if (secondary)` read correctly and was wrong:
    // the rule on `searchFields` is that a column DECLARING a secondary contributes both values, and
    // a per-row test applies it only to rows whose secondary happens to resolve. A row without one
    // then loses its PRIMARY from the search text while its siblings keep theirs — findable or not
    // depending on a value that has nothing to do with the term being searched. Being consulted for
    // some rows is not the same property as covering the column.
    if (col.secondary) columnSearch.push(text, secondary);
  }

  // CAOS-704 — a card's facts, read like every other value on the row and kept apart from `columns`.
  const metrics = spec.cardFields?.map((f) => ({ value: String(readJsonPath(row, f.valueAt) ?? ''), label: f.label }));

  return {
    id,
    title,
    ...(subtitle !== undefined ? { subtitle } : {}),
    ...(href !== undefined ? { href } : {}),
    ...(stageValue !== undefined ? { stageValue } : {}),
    ...(groups !== undefined ? { groups } : {}),
    // CAOS-704 — what a search matches is the author's to say; the title, subtitle and id when they have not.
    //
    // CAOS-1717 — PLUS both values of any column that declares a `secondary`, appended rather than
    // substituted. This ticket was found from a roster that put `appContext` in `searchFields` and
    // showed it nowhere: a reporter could search for where they filed something and never read it.
    // Making the value readable as a second line is half the repair; the other half is that moving
    // it out of `searchFields` — the natural next edit, now that it is on screen — must not take it
    // out of search. Only columns WITH a secondary contribute, so no roster's existing search
    // behaviour changes, and both lines are included because the state this replaces was one of the
    // two being findable and the other not.
    searchText: [
      spec.searchFields
        ? spec.searchFields.map((path) => String(readJsonPath(row, path) ?? '')).join(' ')
        : `${title} ${subtitle ?? ''} ${id}`,
      ...columnSearch,
    ]
      .filter((part) => part !== '')
      .join(' '),
    ...(metrics !== undefined ? { metrics } : {}),
    detail,
    cells,
  };
}

/**
 * Render an authored `rosterPage`: fetch its read route, project every row through the spec's declared
 * paths, and draw the shared list surface. Returns `{ root, done }` so a test can await the initial
 * fetch before asserting — the same shape every hand-written Setup browse already returns.
 */
/**
 * Build the segmented filter-tab bar (spec.filters), wired to call `onChange` with the chosen index.
 *
 * CAOS-544 — exported so a roster-detail page's own table tabs are drawn by THIS function rather
 * than by a second one that looks the same. Two tab bars that drift apart would be two things a
 * reader has to learn, and the schema already shares one `RosterFilterTab` shape between them.
 */
export function buildFilterTabs(
  filters: readonly RosterFilter[],
  active: number,
  onChange: (i: number) => void,
): HTMLElement {
  const bar = document.createElement('div');
  bar.className = 'roster-filter-tabs';
  bar.setAttribute('role', 'tablist');
  filters.forEach((f, i) => {
    const tab = document.createElement('button');
    tab.type = 'button';
    tab.className = 'roster-filter-tab' + (i === active ? ' roster-filter-tab--active' : '');
    tab.setAttribute('role', 'tab');
    tab.setAttribute('aria-selected', i === active ? 'true' : 'false');
    tab.textContent = f.label;
    tab.addEventListener('click', () => onChange(i));
    bar.append(tab);
  });
  return bar;
}

/** CAOS-698 — one row of a roster's hierarchy, in walk order, with how deep it stands. */
export interface HierarchyNode {
  readonly row: Record<string, unknown>;
  readonly id: string;
  readonly depth: number;
}

/**
 * CAOS-698 — order a roster's rows into a depth-first walk of the tree `parentField` describes.
 *
 * Siblings keep the order the rows arrived in. A row whose parent is absent, empty, or names no row
 * here is a root, so an orphan is shown at the top level rather than hidden. A parent cycle cannot
 * loop the walk, and every row the walk never reaches is appended at the top level, so none is dropped.
 */
export function orderHierarchy(
  rows: readonly Record<string, unknown>[],
  idField: string,
  parentField: string,
): HierarchyNode[] {
  const idOf = (row: Record<string, unknown>): string => String(readJsonPath(row, idField) ?? '');
  const known = new Set(rows.map(idOf));
  const byParent = new Map<string | null, Record<string, unknown>[]>();
  for (const row of rows) {
    const raw = readJsonPath(row, parentField);
    const parent = raw != null && raw !== '' && known.has(String(raw)) ? String(raw) : null;
    byParent.set(parent, [...(byParent.get(parent) ?? []), row]);
  }
  const out: HierarchyNode[] = [];
  const seen = new Set<Record<string, unknown>>();
  const walk = (parent: string | null, depth: number): void => {
    for (const row of byParent.get(parent) ?? []) {
      if (seen.has(row)) continue;
      seen.add(row);
      out.push({ row, id: idOf(row), depth });
      walk(idOf(row), depth + 1);
    }
  };
  walk(null, 0);
  for (const row of rows) if (!seen.has(row)) out.push({ row, id: idOf(row), depth: 0 });
  return out;
}

/** CAOS-698 — a tree row's count, worded by how many. Zero is worded, never blank: "No users" is a fact. */
function hierarchyCount(count: NonNullable<RosterHierarchy['count']>, row: Record<string, unknown>): string {
  const read = Number(readJsonPath(row, count.valueAt) ?? 0);
  const n = Number.isFinite(read) ? read : 0;
  return (n === 0 ? count.zero : n === 1 ? count.one : count.other).split('{count}').join(String(n));
}

/**
 * CAOS-591 — the roster's rows as the NODES of a placed tree.
 *
 * {@link orderHierarchy} still does the reading of `parentField`, and keeps every guarantee it had:
 * siblings in the order they arrived, an orphan at the top level rather than hidden, and a parent
 * cycle that neither loops nor drops a row. The NESTING is derived from the depths it already
 * returns rather than walked a second time here, so a second reading cannot disagree with the first.
 */
function hierarchyNodes(
  spec: RosterPageSpec,
  hierarchy: RosterHierarchy,
  rows: readonly Record<string, unknown>[],
): CaosTreeNode[] {
  // A node's `children` is readonly on the way out, so the tree is assembled in a scratch shape and
  // sealed once, rather than by pushing into the thing that is meant not to change.
  type Building = { node: CaosTreeNode; kids: Building[] };
  const roots: Building[] = [];
  // The row most recently seen at each depth — the one a row of the next depth belongs under.
  const openAt: Building[] = [];

  for (const entry of orderHierarchy(rows, spec.idField, hierarchy.parentField)) {
    const built: Building = {
      node: {
        id: entry.id,
        label: String(readJsonPath(entry.row, spec.titleField) ?? entry.id),
        ...(spec.detailRoute ? { href: fillPath(spec.detailRoute, { id: entry.id, ...entry.row }).path } : {}),
        ...(hierarchy.count ? { badge: hierarchyCount(hierarchy.count, entry.row) } : {}),
      },
      kids: [],
    };
    const parent = entry.depth > 0 ? openAt[entry.depth - 1] : undefined;
    if (parent) parent.kids.push(built);
    else roots.push(built);
    openAt[entry.depth] = built;
    openAt.length = entry.depth + 1;
  }

  // A row with nothing under it stays a LEAF: `children` is absent rather than empty, because in this
  // component an empty `children` still means "a branch", and a childless role is not one.
  const seal = (b: Building): CaosTreeNode =>
    b.kids.length === 0 ? b.node : { ...b.node, children: b.kids.map(seal) };
  return roots.map(seal);
}

/**
 * CAOS-591 — the roster's rows as a PLACED tree.
 *
 * This drew its own `<ul role="tree">`, with its own indentation, branch glyph and count span, beside
 * a component whose whole job is drawing exactly that. Two drawings of one idea drift, and these had:
 * the component grew a keyboard model, ARIA levels and expand/collapse that this markup never got, so
 * the Roles hierarchy was the one tree on the platform a keyboard could not move through. Placing the
 * component is what stops that happening again — and is why the hand-drawn version is gone rather
 * than kept beside it.
 */
function renderHierarchy(
  spec: RosterPageSpec,
  hierarchy: RosterHierarchy,
  rows: readonly Record<string, unknown>[],
  navigate?: Navigate,
): HTMLElement {
  const section = document.createElement('caos-detail-section');
  section.setAttribute('heading', hierarchy.heading);
  const tree = document.createElement('caos-tree');
  tree.setAttribute('aria-label', hierarchy.heading);
  // Deep enough to be worth putting away, and every branch still starts open, so the whole shape is
  // visible on arrival exactly as the hand-drawn version showed it.
  tree.setAttribute('collapsible', '');
  (tree as unknown as { nodes: CaosTreeNode[] }).nodes = hierarchyNodes(spec, hierarchy, rows);
  // The tree holds no routing: it says which node was chosen, and the roster decides what that means.
  tree.addEventListener('tree-choose', (ev) => {
    const detail = (ev as CustomEvent<{ href?: string }>).detail;
    if (detail?.href) navigate?.(detail.href);
  });
  section.append(tree);
  return section;
}

export function renderRosterPage(
  spec: RosterPageSpec,
  ctx: {
    readonly navigate?: Navigate;
    readonly fetch?: RosterFetch;
    readonly actionFetch?: ActionFetch;
    readonly saveFile?: SaveFile;
  },
): { root: HTMLElement; done: Promise<void> } {
  const root = document.createElement('div');
  root.className = 'setup-pane';
  const loading = document.createElement('caos-empty-state');
  loading.setAttribute('title', 'Loading…');
  root.append(loading);

  const doFetch = ctx.fetch ?? defaultRosterFetch;
  const doActionFetch = ctx.actionFetch ?? defaultRosterActionFetch;
  const doSaveFile = ctx.saveFile ?? defaultSaveFile;

  const done = (async (): Promise<void> => {
    let outcome = await fetchRows(spec, doFetch);
    root.replaceChildren();

    let activeFilter = spec.filterDefault ?? 0;
    let tabsBar: HTMLElement | undefined;

    async function refresh(): Promise<void> {
      outcome = await fetchRows(spec, doFetch);
      draw();
    }

    function draw(): void {
      /*
       * CAOS-1739 — a failed read draws the list with no rows and a sentence saying so.
       *
       * No rows rather than no list: the header, the search box and the mode switch stay, because
       * the page is still the page and a reader who presses Refresh must have something to press.
       * What changes is the one sentence in the middle, and `readFailed` is carried to the
       * `emptyLabel` callback below rather than being re-derived there.
       */
      const readFailed = !outcome.ok;
      const rows = outcome.ok ? outcome.rows : [];
      // CAOS-1646 R2 — the gate asks about the ACTIVE tab rather than about `filterField`, so a
      // roster whose tabs ask of a folded column narrows properly instead of drawing tabs that
      // move the highlight and leave every row on screen.
      const filtered = canNarrow(spec.filters, activeFilter, spec.filterField)
        ? rows.filter((r) =>
            rowMatchesFilter(
              spec.filters![activeFilter]!,
              filterTabValue(r, spec.filters![activeFilter]!, spec.columns, spec.filterField),
            ),
          )
        : rows;
      // CAOS-1501 — the per-row action, drawn as a trailing column of buttons.
      //
      // ── WHICH READINGS DRAW IT ────────────────────────────────────────────────────────────
      // `table` and `group` do; `card`, `kanban` and `split` do not. That is not a choice made
      // here so much as one the surface already made: `group` is literally the same `tableOf()`
      // repeated once per lane (collection-surface.ts), so a trailing column appears there for
      // free and correctly. The other three lay a row out as a tile or a lane entry with no
      // column strip to trail, and a button that silently disappears when the reader switches
      // reading is worse than one that was never offered there. A roster needing the action in
      // every reading wants a `pageActions` entry, drawn in the header every reading has.
      //
      // The button itself is `renderRosterActionButton` — the SAME one a rosterDetailPage table's
      // `rowAction` uses. The confirm/form dialog, the POST, the follow-up-field path and the
      // error reporting are subtle and already correct once; a second copy here would drift.
      const rowActionColumn =
        spec.rowAction && spec.rowIdField
          ? { key: ROW_ACTION_COLUMN, label: 'Actions', align: 'end' as const, sortable: false }
          : undefined;

      const collectionRows = filtered.map((r) => {
        const projected = projectRow(spec, r, ctx.navigate);
        if (!rowActionColumn) return projected;
        // `visibleWhen` is honoured per row, the way roster-detail-page.ts honours it per record:
        // a row the action does not apply to gets an empty cell, not a button that fails on click.
        if (!actionVisible(spec.rowAction!, r)) return projected;
        const rowId = String(readJsonPath(r, spec.rowIdField!) ?? '');
        const button = renderRosterActionButton({
          action: spec.rowAction!,
          record: r,
          // On a roster the ROW is the record, so `{recordId}` resolves to the row's own `idField`
          // and `{rowId}` to its `rowIdField`. The two are usually different values — Installed
          // Packages navigates by row id and uninstalls by package name — which is exactly why
          // `rowIdField` exists as its own path rather than reusing `idField`.
          recordId: projected.id,
          rowId,
          fetch: doActionFetch,
          // CAOS-1837 — the SAME seam the download button below uses, so a row action whose
          // response carries a file (Data Management's "Export records") saves it the same way and
          // a test measures it the same way. Not a second route to the disk.
          saveFile: doSaveFile,
          onSuccess: () => void refresh(),
        });
        button.dataset['rowId'] = rowId;
        return { ...projected, cells: { ...projected.cells, [ROW_ACTION_COLUMN]: { node: button } } };
      });

      const actionButtons = (spec.pageActions ?? []).map((action) =>
        renderRosterActionButton({
          action,
          record: {},
          recordId: '',
          fetch: doActionFetch,
          saveFile: doSaveFile,
          onSuccess: () => void refresh(),
        }),
      );

      // CAOS-551 — the export, drawn beside the actions and only when the roster asked for one.
      //
      // It writes `filtered`, not `rows`: the active tab is a statement about WHICH PEOPLE the
      // reader means, and an export that quietly widened that back to everybody would answer a
      // different question from the screen it was pressed on. The search box is not part of that —
      // it finds a row within the set, it lives inside the shared list surface, and what it has
      // narrowed to is not something this renderer is told.
      if (spec.download) {
        const download = spec.download;
        const btn = rosterButton(download.label ?? 'Export');
        btn.addEventListener('click', () => doSaveFile(download.filename, rosterCsv(spec.columns, filtered)));
        actionButtons.push(btn);
      }

      // Drawn whenever a tab could narrow — the same question `canNarrow` asks of the active tab,
      // asked of the set, so the bar and the filtering cannot disagree about whether tabs apply.
      if (spec.filters && spec.filters.some((_, i) => canNarrow(spec.filters, i, spec.filterField))) {
        tabsBar = buildFilterTabs(spec.filters, activeFilter, (i) => {
          activeFilter = i;
          draw();
        });
      }

      const { root: surface } = renderCollection({
        key: `roster.${spec.key}`,
        ...(spec.eyebrow !== undefined ? { eyebrow: spec.eyebrow } : {}),
        heading: spec.label,
        ...(spec.description !== undefined ? { description: spec.description } : {}),
        columns: [
          ...spec.columns.map((c) => ({ key: c.key, label: c.label, ...(c.align ? { align: c.align } : {}) })),
          ...(rowActionColumn ? [rowActionColumn] : []),
        ],
        rows: collectionRows,
        ...(spec.searchPlaceholder !== undefined ? { searchPlaceholder: spec.searchPlaceholder } : {}),
        ...(spec.searchFields?.length === 0 ? { searchable: false } : {}),
        ...(spec.stages !== undefined ? { stages: spec.stages } : {}),
        ...(spec.groupBy !== undefined ? { groupBy: spec.groupBy.map(({ key, label }) => ({ key, label })) } : {}),
        ...(spec.groupByDefault !== undefined ? { groupByDefault: spec.groupByDefault } : {}),
        ...(spec.sortOptions !== undefined ? { sortOptions: spec.sortOptions } : {}),
        ...(spec.defaultMode !== undefined ? { defaultMode: spec.defaultMode } : {}),
        ...(actionButtons.length > 0 ? { actions: actionButtons } : {}),
        ...(tabsBar !== undefined ? { filters: tabsBar } : {}),
        /*
         * CAOS-1739 — a failed read wins over BOTH empty wordings, including the search one.
         *
         * The search case is the one worth spelling out. With a term typed and a read that failed,
         * the old code reached `searchEmptyLabel` and said "No installed package matches
         * \"slate\"" — a claim about the contents of a list it never managed to read, and a more
         * specific falsehood than the plain empty label is. So this is checked FIRST rather than
         * after the search branch.
         */
        emptyLabel: (s) =>
          readFailed
            ? READ_FAILED_LABEL
            : s.search.trim() !== ''
              ? (spec.searchEmptyLabel ?? spec.emptyLabel).replace('{search}', s.search.trim())
              : spec.emptyLabel,
        onOpen: spec.detailRoute ? (id) => ctx.navigate?.(fillPath(spec.detailRoute!, { id }).path) : undefined,
        // CAOS-698 — the tree is a second reading of the rows on screen, so a roster with none draws no tree.
        ...(spec.hierarchy !== undefined && filtered.length > 0
          ? { footer: [renderHierarchy(spec, spec.hierarchy, filtered, ctx.navigate)] }
          : {}),
      });
      root.replaceChildren(surface);
    }

    draw();
  })();

  return { root, done };
}
